<template>
  <div class="container mx-auto">
    <div>
      <doe-page-heading
        label="Confirm your details"
        description="Make sure your personal information has been entered correctly."
        class="mb-2"
      />
      <doe-button
        @click="editAccountDetails()"
        class="mb-4"
        appearance="secondary"
        size="small"
        label="Edit personal details"
      />
      <doe-banner label="Personal details" class="mb-6">
        <p class="font-sans text-blue-900 mb-2 md:mb-0">
          <span class="font-bold w-full md:w-1/2 inline-block">First name:</span>
          <span>{{ registrationUser.firstName }}</span>
        </p>
        <p class="font-sans text-blue-900 mb-2 md:mb-0">
          <span class="font-bold w-full md:w-1/2 inline-block">Middle name:</span>
          <span v-if="registrationUser.middleName">{{ registrationUser.middleName }}</span>
          <span v-else class="pl-1">
            &mdash;
          </span>
        </p>
        <p class="font-sans text-blue-900 mb-2 md:mb-0">
          <span class="font-bold w-full md:w-1/2 inline-block">Last name:</span>
          <span>{{ registrationUser.lastName }}</span>
        </p>
        <p class="font-sans text-blue-900 mb-2 md:mb-0">
          <span class="font-bold w-full md:w-1/2 inline-block">Preferred name:</span>
          <span v-if="registrationUser.preferredName">{{ registrationUser.preferredName }}</span>
          <span v-else class="pl-1">
            &mdash;
          </span>
        </p>
        <p class="font-sans text-blue-900 mb-2 md:mb-0">
          <span class="font-bold w-full md:w-1/2 inline-block">Date of birth:</span>
          <span>{{ handleDate(registrationUser.dateOfBirth) }}</span>
        </p>
        <p class="font-sans text-blue-900 mb-2 md:mb-0">
          <span class="font-bold w-full md:w-1/2 inline-block">Personal email address:</span>
          <span>{{ registrationUser.email }}</span>
        </p>
        <p class="font-sans text-blue-900 mb-2 md:mb-0">
          <span class="font-bold w-full md:w-1/2 inline-block">Mobile number:</span>
          <span v-if="registrationUser.mobile">{{ registrationUser.mobile }}</span>
          <span v-else class="pl-1">
            &mdash;
          </span>
        </p>
      </doe-banner>

      <doe-paragraph
        description="Make sure the details below match your identity documents."
        class="mb-2"
      />
      <doe-button @click="editIdentityDocuments()" class="mb-4" appearance="secondary" size="small" label="Edit identity documents" />
      <div v-for="(option, index) in selectedIdentities" :key="index">
        <doe-banner v-if="option.value !== 'regodvs'" :label="option.text" class="mb-6">
          <div
            v-for="(option, index) in option.schema"
            :key="`${option.value}-${index}`"
          >
            <p
              class="font-sans text-blue-900 mb-2 md:mb-0"
              v-if="option.fieldType === 'FieldText' || option.fieldType === 'Radio' || option.fieldType === 'Dropdown'"
            >
              <span class="font-bold w-full md:w-1/2 inline-block"
                >{{ option.label }}:</span
              >
              <span v-if="registrationUser[option.name]">
                {{ registrationUser[option.name] }}
              </span>
              <span v-else class="pl-1">
                &mdash;
              </span>
            </p>
            <p
              class="font-sans text-blue-900 mb-2 md:mb-0"
              v-if="option.fieldType === 'Date'"
            >
              <span class="font-bold w-full md:w-1/2 inline-block"
                >{{ option.label }}:</span
              >
              <span v-if="registrationUser[option.name]">
                {{ handleDate(registrationUser[option.name]) }}
              </span>
              <span v-else class="pl-1">
                &mdash;
              </span>
            </p>
            <p
              class="font-sans text-blue-900 mb-2 md:mb-0"
              v-if="option.fieldType === 'DateMedicare'"
            >
              <span class="font-bold w-full md:w-1/2 inline-block"
                >{{ option.label }}:</span
              >
              <span v-if="registrationUser[option.name]">
                {{ handleMedicareDate(registrationUser[option.name], registrationUser.greenid_medicaredvs_cardColour) }}
              </span>
              <span v-else class="pl-1">
                &mdash;
              </span>
            </p>
          </div>
        </doe-banner>

        <doe-banner v-else :label="option.text" class="mb-6">
          <div
            v-for="(option, index) in option.schema"
            :key="`${option.value}-${index}`"
          >
            <p
              class="font-sans text-blue-900 mb-2 md:mb-0"
              v-if="option.fieldType === 'Dropdown'"
            >
              <span class="font-bold w-full md:w-1/2 inline-block"
                >{{ option.label }}:</span
              >
              <span v-if="registrationUser[option.name]">
                {{ handleStateValue(registrationUser[option.name]) }}
              </span>
              <span v-else class="pl-1">
                &mdash;
              </span>
            </p>
            <p
              class="font-sans text-blue-900 mb-2 md:mb-0"
              v-if="option.fieldType === 'Date'"
            >
              <span class="font-bold w-full md:w-1/2 inline-block"
                >{{ option.label }}:</span
              >
              <span v-if="registrationUser[option.name]">
                {{ handleDate(registrationUser[option.name]) }}
              </span>
              <span v-else class="pl-1">
                &mdash;
              </span>
            </p>
          </div>

          <div
            v-for="(option, index) in findRegoFormSchema(registrationUser.greenid_regodvs_state)"
            :key="`${option.value}-${index}-rego`"
          >
            <p
              class="font-sans text-blue-900 mb-2 md:mb-0"
              v-if="option.fieldType === 'FieldText'"
            >
              <span class="font-bold w-full md:w-1/2 inline-block"
                >{{ option.label }}:</span
              >
              <span v-if="registrationUser[option.name]">
                {{ registrationUser[option.name] }}
              </span>
              <span v-else class="pl-1">
                &mdash;
              </span>
            </p>
            <p
              class="font-sans text-blue-900 mb-2 md:mb-0"
              v-if="option.fieldType === 'Date'"
            >
              <span class="font-bold w-full md:w-1/2 inline-block"
                >{{ option.label }}:</span
              >
              <span v-if="registrationUser[option.name]">
                {{ handleDate(registrationUser[option.name]) }}
                <span class="text-red-900 text-sm" v-if="registrationUser.dateOfBirth !== registrationUser[option.name]"><doe-icon icon="circleExclaimation" class="mt-1" /></span>
              </span>
              <span v-else class="pl-1">
                &mdash;
              </span>
            </p>
          </div>
        </doe-banner>
      </div>

      <div class="max-w-4xl mt-6">
        <form-generator
          :schema="schema"
          @form:previous="formPrevious()"
          @form:next="formNext()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import schema from "@/schemas/register/registerConfirm";
import { IDENTITY_OPTIONS } from "@/data/options";
import FormGenerator from "@/components/form/FormGenerator";
import DoePageHeading from "@/components/typography/PageHeading";
import DoeBanner from "@/components/banner/Banner";
import DoeParagraph from "@/components/typography/Paragraph";
import DoeButton from "@/components/button/Button";
import DoeIcon from "@/components/icon/Icon";

export default {
  name: "AuthConfirm",
  inject: {
    $validator: "$validator"
  },
  components: {
    FormGenerator,
    DoePageHeading,
    DoeBanner,
    DoeParagraph,
    DoeButton,
    DoeIcon
  },
  data() {
    return {
      schema: schema,
      options: IDENTITY_OPTIONS,
      counter: 1,
    };
  },
  computed: {
    ...mapGetters(["getRegistrationUser", "getIdentities"]),
    registrationUser() {
      return this.getRegistrationUser;
    },
    identities() {
      return this.getIdentities;
    },
    selectedIdentities() {
      return this.options.filter(option =>
        this.identities.includes(option.value)
      );
    }
  },
  mounted() {
  },
  methods: {
    findRegoFormSchema(stateRego) {
      const regoForms = this.options.filter(
        option => option.value === "regodvs"
      );
      const regoForm = regoForms[0].options.filter(
        option => option.value === stateRego
      );
      return regoForm[0].schema;
    },
    handleDate(timestamp) {
      var today = new Date(timestamp);
      var dd = String(today.getDate()).padStart(2, '0');
      var mm = String(today.getMonth() + 1).padStart(2, '0');
      var yyyy = today.getFullYear();
      return dd + '/' + mm + '/' + yyyy;
    },
    handleMedicareDate(timestamp, colour) {
      var today = new Date(timestamp);
      var dd = String(today.getDate()).padStart(2, '0');
      var mm = String(today.getMonth() + 1).padStart(2, '0');
      var yyyy = today.getFullYear();
      if(colour === 'G') {
        return mm + '/' + yyyy;
      } else {
        return dd + '/' + mm + '/' + yyyy;
      }
    },
    handleStateValue(state) {
      return state.replace("regodvs", "").toUpperCase();
    },
    formPrevious() {
      this.$router.push({ name: "register-identity" });
    },
    formNext() {
      this.$router.push({ name: "register-complete" });
    },
    editAccountDetails() {
      this.$router.push({ name: "register-account" });
    },
    editIdentityDocuments() {
      this.$router.push({ name: "register-identity" });
    }
  }
};
</script>

<style lang="scss" scoped></style>
